import { SnowflakeSink } from './snowflake.copysink';
import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { ModelConstants } from '../model-constants';
import { SnowflakeV2TableDataset } from '../dataset/snowflake-v2.dataset';
import { ICopySinkWithUpdateMethod } from '../copysink/copysink';
import {
    UpdateMethod,
    UpdateMethodType,
    writeBehaviorOptionValues,
    writeBehaviorOptionValueTyping
} from '../../constants/write-behavior-constants';
import { ValueAdapter } from '../adapters/value.adapter';
import { validateUniqueKeyColumns } from './copysink.util';
import { shellStringResources } from '../../Resources/ShellStringResources';

@Metamodel.model({
    name: ModelConstants.snowflakeV2CopySink,
    display: [],
    referable: true,
    tags: { datasetModel: Metamodel.modelFor(SnowflakeV2TableDataset) }
})
export class SnowflakeV2Sink extends SnowflakeSink implements ICopySinkWithUpdateMethod {
    public static readonly MODEL: IModel;
    public static readonly WRITE_BEHAVIOR: IAttribute;
    public static readonly WRITE_BEHAVIOR_KEYS: IAttribute;
    public static readonly UPSERT_SETTINGS: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR'
    })
    public writeBehavior?: writeBehaviorOptionValueTyping;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_KEYS',
        accepts: ['Array', 'Object'],
        constraint: {
            validator: SnowflakeV2Sink.validateKeyColumnsMapping
        }
    })
    public writeBehaviorKeys?: string[];

    public get updateMethod() {
        switch (this.writeBehavior) {
            case undefined:
                return UpdateMethod.Append;
            case writeBehaviorOptionValues.upsert:
                return UpdateMethod.Upsert;
            case writeBehaviorOptionValues.SCD2:
                return UpdateMethod.SCD2;
            default:
                return undefined;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Append:
                this.writeBehavior = undefined;
                break;
            case UpdateMethod.Upsert:
                this.writeBehavior = writeBehaviorOptionValues.upsert;
                break;
            case UpdateMethod.SCD2:
                this.writeBehavior = writeBehaviorOptionValues.SCD2;
                break;
            default:
        }
    }

    public get upsertKeyColumns() {
        return this.writeBehaviorKeys;
    }

    public set upsertKeyColumns(value: string[]) {
        this.writeBehaviorKeys = value;
    }

    public static validateKeyColumnsMapping(sink: SnowflakeV2Sink, updateMethodLabel?: string): IViolation[] {
        const isCopyJob = sink.owner != null && Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity;
        const writeBehavior = ValueAdapter.toResource(sink, SnowflakeV2Sink.WRITE_BEHAVIOR);
        if (updateMethodLabel == null) {
            updateMethodLabel =
                writeBehavior === writeBehaviorOptionValues.SCD2
                    ? shellStringResources.WriteBehavior_Option_SCD2
                    : isCopyJob
                      ? shellStringResources.MergeLabel
                      : shellStringResources.WriteBehavior_Option_Upsert;
        }
        if (writeBehavior !== writeBehaviorOptionValues.upsert && writeBehavior !== writeBehaviorOptionValues.SCD2) {
            // only check for key columns mapping when certain write behavior is upsert
            return [];
        }
        return validateUniqueKeyColumns(
            sink,
            undefined,
            sink.writeBehaviorKeys ?? [],
            isCopyJob,
            updateMethodLabel
        );
    }
}
